<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { extractServerError } from 'dashboard/helper/appointmentFormErrors';
import ProfessionalForm from './ProfessionalForm.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const store = useStore();
const { t } = useI18n();
const { showAlert } = useAlert();

const searchQuery = ref('');
const showForm = ref(false);
const showDeleteConfirm = ref(false);
const selectedProfessional = ref(null);
// AP-03: backend validation message shown inside the open form on save failure.
const formError = ref('');

const professionals = computed(
  () => store.getters['professionals/getProfessionals']
);
const uiFlags = computed(() => store.getters['professionals/getUIFlags']);

const filteredProfessionals = computed(() => {
  const q = searchQuery.value.toLowerCase().trim();
  if (!q) return professionals.value;
  return professionals.value.filter(
    p =>
      p.name?.toLowerCase().includes(q) ||
      p.specialty?.toLowerCase().includes(q) ||
      p.email?.toLowerCase().includes(q)
  );
});

onMounted(() => {
  store.dispatch('professionals/get');
});

const openCreateForm = () => {
  selectedProfessional.value = null;
  formError.value = '';
  showForm.value = true;
};

const openEditForm = professional => {
  selectedProfessional.value = professional;
  formError.value = '';
  showForm.value = true;
};

const closeForm = () => {
  showForm.value = false;
  selectedProfessional.value = null;
  formError.value = '';
};

const confirmDelete = professional => {
  selectedProfessional.value = professional;
  showDeleteConfirm.value = true;
};

const handleFormSubmit = async data => {
  formError.value = '';
  try {
    if (selectedProfessional.value?.id) {
      await store.dispatch('professionals/update', {
        id: selectedProfessional.value.id,
        ...data,
      });
      showAlert(t('APPOINTMENTS.PROFESSIONALS.UPDATED'));
    } else {
      await store.dispatch('professionals/create', data);
      showAlert(t('APPOINTMENTS.PROFESSIONALS.CREATED'));
    }
    closeForm();
  } catch (error) {
    // AP-03: surface the backend validation message inside the open form.
    formError.value = extractServerError(error) || t('APPOINTMENTS.ERROR');
    showAlert(formError.value);
  }
};

const handleDelete = async () => {
  try {
    await store.dispatch('professionals/delete', selectedProfessional.value.id);
    showAlert(t('APPOINTMENTS.PROFESSIONALS.DELETED'));
  } catch (error) {
    showAlert(t('APPOINTMENTS.ERROR'));
  } finally {
    showDeleteConfirm.value = false;
    selectedProfessional.value = null;
  }
};
</script>

<template>
  <div class="flex flex-col flex-1 overflow-hidden bg-n-background">
    <!-- Toolbar: search + CTA -->
    <div
      class="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 px-6 lg:px-8 py-5 border-b border-n-weak/50 bg-n-alpha-1/10"
    >
      <div class="relative flex-1 max-w-md group">
        <span
          class="i-lucide-search absolute left-4 top-1/2 -translate-y-1/2 size-4.5 text-n-slate-10 group-focus-within:text-n-brand transition-colors pointer-events-none"
          aria-hidden="true"
        />
        <input
          v-model="searchQuery"
          type="search"
          :placeholder="t('APPOINTMENTS.SEARCH_PLACEHOLDER')"
          :aria-label="t('APPOINTMENTS.SEARCH_PLACEHOLDER')"
          class="w-full border border-n-strong rounded-xl !pl-11 !pr-4 !mb-0 py-2.5 text-sm font-medium bg-n-background text-n-slate-12 transition-all shadow-sm hover:bg-n-alpha-1/50 focus:bg-n-background focus:outline-none focus:ring-2 focus:ring-n-brand/20 focus:border-n-brand placeholder-n-slate-9"
        />
      </div>
      <button
        type="button"
        class="inline-flex items-center justify-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm hover:shadow-lg hover:shadow-n-brand/20 hover:-translate-y-px active:translate-y-0 transition-all focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-n-brand whitespace-nowrap"
        @click="openCreateForm"
      >
        <span class="i-lucide-plus size-4.5" aria-hidden="true" />
        {{ t('APPOINTMENTS.PROFESSIONALS.ADD') }}
      </button>
    </div>

    <div
      class="flex-1 overflow-y-auto px-6 lg:px-8 py-6 relative custom-scrollbar"
    >
      <!-- Fetch error banner -->
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 -translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
      >
        <div
          v-if="uiFlags.isFetchError"
          class="rounded-xl border border-n-ruby-5 bg-gradient-to-r from-n-ruby-2 to-n-ruby-1 text-n-ruby-11 px-5 py-4 mb-5 text-sm flex items-center gap-3 shadow-sm"
          data-testid="fetch-error-banner"
        >
          <span class="i-lucide-alert-circle size-5" aria-hidden="true" />
          <span class="font-medium">{{ t('APPOINTMENTS.ERROR_FETCH') }}</span>
          <button
            type="button"
            class="ml-auto bg-n-ruby-3 hover:bg-n-ruby-4 text-n-ruby-12 px-4 py-1.5 rounded-lg font-semibold transition-colors"
            @click="store.dispatch('professionals/get')"
          >
            {{ t('APPOINTMENTS.RETRY') }}
          </button>
        </div>
      </Transition>

      <!-- Loading skeleton Premium -->
      <div
        v-if="uiFlags.isFetching"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        <div
          v-for="i in 4"
          :key="`skeleton-${i}`"
          class="h-32 rounded-[1.25rem] bg-gradient-to-r from-n-alpha-1 to-n-alpha-2 animate-pulse ring-1 ring-black/5"
        >
          <div class="p-5 flex flex-col gap-3 h-full">
            <div class="flex items-center gap-3">
              <div class="size-12 rounded-full bg-n-background/50" />
              <div class="space-y-2 flex-1">
                <div class="h-4 bg-n-background/50 rounded w-3/4" />
                <div class="h-3 bg-n-background/50 rounded w-1/2" />
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <Transition
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 translate-y-4"
      >
        <div
          v-if="!uiFlags.isFetching && filteredProfessionals.length === 0"
          class="flex flex-col items-center justify-center py-20 text-center rounded-[1.25rem] border border-dashed border-n-strong/50 bg-n-alpha-1/30"
        >
          <div
            class="size-20 rounded-3xl bg-n-alpha-2 flex items-center justify-center mb-5 ring-1 ring-white/10 shadow-inner"
          >
            <span
              class="i-lucide-user-cog size-10 text-n-slate-9"
              aria-hidden="true"
            />
          </div>
          <h3 class="text-lg font-bold text-n-slate-12 mb-2 tracking-tight">
            {{ t('APPOINTMENTS.PROFESSIONALS.EMPTY_STATE') }}
          </h3>
          <p
            class="text-sm font-medium text-n-slate-10 max-w-sm leading-relaxed mb-5"
          >
            {{
              searchQuery.trim()
                ? t(
                    'APPOINTMENTS.PROFESSIONALS.EMPTY_SEARCH_HINT',
                    'Tente outro termo de busca.'
                  )
                : t(
                    'APPOINTMENTS.PROFESSIONALS.EMPTY_HINT',
                    'Cadastre profissionais para começar a agendar atendimentos.'
                  )
            }}
          </p>
          <button
            v-if="!searchQuery.trim()"
            type="button"
            class="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-n-brand hover:bg-n-brand/90 rounded-xl shadow-sm transition-all focus:outline-none focus:ring-2 focus:ring-n-brand"
            @click="openCreateForm"
          >
            <span class="i-lucide-plus size-4.5" aria-hidden="true" />
            {{ t('APPOINTMENTS.PROFESSIONALS.ADD') }}
          </button>
        </div>
      </Transition>

      <!-- Grid Cards Premium -->
      <div
        v-if="!uiFlags.isFetching && filteredProfessionals.length > 0"
        class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4"
      >
        <div
          v-for="professional in filteredProfessionals"
          :key="professional.id"
          class="group relative flex flex-col p-5 rounded-[1.25rem] border border-n-weak bg-n-background hover:bg-n-alpha-1/20 transition-all duration-300 shadow-sm hover:shadow-md hover:-translate-y-1 hover:border-n-brand/30 overflow-hidden"
        >
          <div
            class="absolute inset-0 bg-gradient-to-br from-n-brand/0 to-n-brand/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
          />

          <div class="flex items-start gap-4 mb-4 relative z-10">
            <div
              class="ring-2 ring-n-background rounded-full shadow-sm relative"
            >
              <Avatar
                :src="professional.avatar_url || ''"
                :name="professional.name || '?'"
                :size="48"
                rounded-full
              />
              <span
                class="absolute bottom-0 right-0 size-3 rounded-full border-2 border-n-background"
                :style="{ backgroundColor: professional.color || '#3B82F6' }"
              />
            </div>
            <div class="flex-1 min-w-0 pt-1">
              <h3
                class="text-[15px] font-bold text-n-slate-12 leading-tight truncate group-hover:text-n-brand transition-colors"
              >
                {{ professional.name }}
              </h3>
              <p
                v-if="professional.specialty"
                class="text-xs font-semibold text-n-slate-10 uppercase tracking-wide mt-1 truncate"
              >
                {{ professional.specialty }}
              </p>
            </div>
          </div>

          <div class="flex-1 flex flex-col relative z-10">
            <div
              v-if="professional.email"
              class="flex items-center gap-2 text-xs font-medium text-n-slate-11 group-hover:text-n-slate-12 transition-colors mb-3 truncate"
            >
              <span
                class="i-lucide-mail size-3.5 flex-shrink-0 text-n-slate-10"
                aria-hidden="true"
              />
              <span class="truncate">{{ professional.email }}</span>
            </div>
          </div>

          <!-- Actions -->
          <div
            class="flex items-center gap-2 pt-4 border-t border-n-weak/50 opacity-0 group-hover:opacity-100 transition-opacity mt-auto relative z-10"
          >
            <button
              type="button"
              class="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors focus:outline-none focus:ring-2 focus:ring-n-slate-6"
              @click="openEditForm(professional)"
            >
              <span class="i-lucide-pencil size-3.5" aria-hidden="true" />
              {{ t('APPOINTMENTS.TABLE.EDIT') }}
            </button>
            <div class="w-px h-4 bg-n-weak/50" />
            <button
              type="button"
              class="flex-1 inline-flex items-center justify-center gap-1.5 py-2 text-xs font-semibold rounded-lg text-n-ruby-10 hover:bg-n-ruby-2 hover:text-n-ruby-11 transition-colors focus:outline-none focus:ring-2 focus:ring-n-ruby-6"
              @click="confirmDelete(professional)"
            >
              <span class="i-lucide-trash-2 size-3.5" aria-hidden="true" />
              {{ t('APPOINTMENTS.TABLE.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modals (Preserved forms) -->
    <ProfessionalForm
      v-if="showForm"
      :professional="selectedProfessional"
      :is-submitting="uiFlags.isCreating || uiFlags.isUpdating"
      :server-error="formError"
      @close="closeForm"
      @submit="handleFormSubmit"
    />

    <Transition
      enter-active-class="transition duration-300 ease-out"
      enter-from-class="opacity-0 scale-95"
      enter-to-class="opacity-100 scale-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100 scale-100"
      leave-to-class="opacity-0 scale-95"
    >
      <div
        v-if="showDeleteConfirm"
        class="fixed inset-0 z-50 flex items-center justify-center bg-n-slate-12/60 backdrop-blur-sm p-4"
      >
        <div
          class="bg-n-background rounded-[1.25rem] shadow-2xl w-full max-w-sm p-6 ring-1 ring-white/10"
        >
          <h3 class="text-lg font-semibold text-n-slate-12 mb-2 tracking-tight">
            {{ t('APPOINTMENTS.PROFESSIONALS.DELETE_CONFIRM_TITLE') }}
          </h3>
          <p class="text-sm text-n-slate-11 mb-5">
            {{ t('APPOINTMENTS.PROFESSIONALS.DELETE_CONFIRM') }}
          </p>
          <div class="flex justify-end gap-3">
            <button
              type="button"
              class="px-5 py-2.5 text-sm font-semibold text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-n-slate-6"
              @click="showDeleteConfirm = false"
            >
              {{ t('APPOINTMENTS.PROFESSIONALS.FORM.CANCEL') }}
            </button>
            <button
              type="button"
              :disabled="uiFlags.isDeleting"
              class="px-5 py-2.5 text-sm font-semibold text-white bg-n-ruby-9 hover:bg-n-ruby-9/90 rounded-xl disabled:opacity-50 transition-all shadow-sm focus:outline-none focus:ring-2 focus:ring-n-ruby-9"
              @click="handleDelete"
            >
              <span
                v-if="uiFlags.isDeleting"
                class="i-lucide-loader-2 size-4 animate-spin inline-block mr-1"
              />
              {{ t('APPOINTMENTS.TABLE.DELETE') }}
            </button>
          </div>
        </div>
      </div>
    </Transition>
  </div>
</template>
